import { motion } from "framer-motion";

/**
 * Animated warung scene illustration — replaces hero photo.
 * Pure SVG + framer-motion, no real images.
 */
export default function HeroIllustration() {
    return (
        <div className="relative w-full aspect-[4/5] rounded-3xl overflow-hidden border border-border bg-gradient-to-br from-[#0A192F] via-[#0c2752] to-[#10B981]/30 shadow-2xl shadow-brand-deep/20">
            {/* Animated background blobs */}
            <motion.div
                animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-brand-green blur-3xl"
            />
            <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-blue-500 blur-3xl"
            />

            {/* Grid pattern */}
            <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 400 500" fill="none">
                <defs>
                    <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
                        <path d="M32 0 L0 0 0 32" stroke="white" strokeWidth="0.5" fill="none" />
                    </pattern>
                </defs>
                <rect width="400" height="500" fill="url(#grid)" />
            </svg>

            {/* Warung illustration */}
            <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 400 500"
                fill="none"
                preserveAspectRatio="xMidYMax meet"
            >
                {/* Ground */}
                <motion.rect
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    x="0" y="420" width="400" height="80" fill="#0a1428"
                />
                <motion.rect
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.6 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    x="0" y="418" width="400" height="4" fill="#10B981"
                />

                {/* Warung shop - roof */}
                <motion.g
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                >
                    <polygon points="60,240 340,240 320,200 80,200" fill="#0A192F" stroke="#10B981" strokeWidth="2" />
                    <polygon points="80,200 320,200 300,180 100,180" fill="#10B981" />
                    {/* Roof stripes */}
                    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                        <line key={i} x1={100 + i * 28} y1="180" x2={80 + i * 28} y2="200" stroke="#0A192F" strokeWidth="1.5" />
                    ))}
                </motion.g>

                {/* Warung body */}
                <motion.rect
                    initial={{ scaleY: 0, opacity: 0 }}
                    animate={{ scaleY: 1, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.35 }}
                    style={{ transformOrigin: "200px 420px" }}
                    x="60" y="240" width="280" height="180" fill="#1E293B"
                />

                {/* Warung counter/window */}
                <motion.rect
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.6 }}
                    x="90" y="280" width="220" height="100" fill="#0A192F" stroke="#10B981" strokeWidth="1.5" rx="6"
                />

                {/* Sign "WARUNG" */}
                <motion.g
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                >
                    <rect x="140" y="248" width="120" height="22" fill="#10B981" rx="4" />
                    <text x="200" y="263" textAnchor="middle" fontSize="13" fontWeight="800" fill="#0A192F" fontFamily="Outfit, sans-serif">WARUNGPAY</text>
                </motion.g>

                {/* Product shelves inside window */}
                {[0, 1, 2].map((row) => (
                    <motion.g
                        key={row}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.85 + row * 0.07 }}
                    >
                        <line x1="100" y1={300 + row * 25} x2="300" y2={300 + row * 25} stroke="#10B981" strokeOpacity="0.3" strokeWidth="1" />
                        {[0, 1, 2, 3, 4, 5].map((c) => (
                            <rect
                                key={c}
                                x={108 + c * 32}
                                y={290 + row * 25}
                                width="24"
                                height="14"
                                fill={["#34D399", "#10B981", "#22d3ee", "#fbbf24", "#f87171"][c % 5]}
                                opacity="0.85"
                                rx="2"
                            />
                        ))}
                    </motion.g>
                ))}

                {/* Counter person silhouette */}
                <motion.g
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 1 }}
                    style={{ transformOrigin: "200px 400px" }}
                >
                    <circle cx="200" cy="395" r="14" fill="#fde68a" />
                    <rect x="186" y="408" width="28" height="24" rx="4" fill="#10B981" />
                </motion.g>
            </svg>

            {/* Floating phone with payment success */}
            <motion.div
                initial={{ x: 30, y: 20, opacity: 0 }}
                animate={{ x: 0, y: [0, -8, 0], opacity: 1 }}
                transition={{
                    x: { duration: 0.6, delay: 1.1 },
                    opacity: { duration: 0.6, delay: 1.1 },
                    y: { duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 1.7 },
                }}
                className="absolute right-6 top-16 w-32 sm:w-36"
            >
                <div className="bg-white rounded-3xl p-3 shadow-2xl shadow-black/30 border-4 border-slate-800 relative">
                    <div className="h-1.5 w-10 rounded-full bg-slate-800 mx-auto mb-2" />
                    {/* Screen */}
                    <div className="rounded-2xl bg-gradient-to-br from-brand-green/10 to-brand-green/5 p-3 text-center">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.4, delay: 1.6, type: "spring", stiffness: 200 }}
                            className="h-10 w-10 rounded-full bg-brand-green flex items-center justify-center mx-auto"
                        >
                            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </motion.div>
                        <div className="font-heading font-bold text-[11px] text-brand-deep mt-2">Sukses</div>
                        <div className="font-heading font-extrabold text-sm text-brand-deep">Rp 50.000</div>
                        <motion.div
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ duration: 0.5, delay: 1.9 }}
                            className="h-0.5 bg-brand-green/30 rounded-full mt-2"
                            style={{ transformOrigin: "left" }}
                        />
                    </div>
                </div>
            </motion.div>

            {/* Floating coins */}
            {[
                { delay: 1.8, x: "20%", y: "30%", size: 24 },
                { delay: 2.2, x: "75%", y: "55%", size: 18 },
                { delay: 2.6, x: "15%", y: "65%", size: 20 },
                { delay: 3.0, x: "85%", y: "25%", size: 16 },
            ].map((c, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30, scale: 0.5 }}
                    animate={{
                        opacity: [0, 1, 1, 0],
                        y: [30, -10, -25, -50],
                        scale: [0.5, 1, 1, 0.8],
                        rotate: [0, 180, 360]
                    }}
                    transition={{ duration: 3, repeat: Infinity, delay: c.delay, ease: "easeOut" }}
                    className="absolute flex items-center justify-center text-amber-300 font-bold font-heading"
                    style={{
                        left: c.x, top: c.y, width: c.size, height: c.size,
                        background: "radial-gradient(circle, #fbbf24 60%, #d97706 100%)",
                        borderRadius: "50%",
                        fontSize: c.size * 0.4,
                        boxShadow: "0 0 20px rgba(251,191,36,0.6), inset 0 -2px 4px rgba(0,0,0,0.2)",
                    }}
                >
                    <span style={{ color: "#78350f" }}>Rp</span>
                </motion.div>
            ))}

            {/* Bottom stat card */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.4 }}
                className="absolute bottom-5 left-5 right-5"
            >
                <div className="bg-white/95 dark:bg-card backdrop-blur-md rounded-2xl p-4 shadow-xl border border-white/20">
                    <div className="text-xs text-muted-foreground">Profit Bulan Ini</div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.7 }}
                        className="font-heading font-bold text-2xl text-brand-green mt-1"
                    >
                        Rp 4.350.000
                    </motion.div>
                    <div className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M7 14l5-5 5 5z" /></svg>
                        +28% dari bulan lalu
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
